import dice from 'fast-dice-coefficient';
import type { ImportDeclaration, ModuleDeclaration } from '~/lib/static-analysis/esm';
import MagicString from '~/lib/static-analysis/magic';
import { renderESM } from '~/lib/static-analysis/render-esm';
import { getOutput } from '~/lib/utils/webcontainer';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import { stripIndents } from '~/utils/stripIndent';
import { removeWorkDir } from '~/utils/workDir';

const targets = new Set([
  'lucide',
  'lucide-react',
  'lucide-svelte',
  'lucide-solid',
  'lucide-vue-next',
  'lucide-preact',
  'lucide-react-native',
  '@lucide/lab',
]);

const iconsMap = new Map<string, Set<string>>();

export function clearIcons() {
  iconsMap.clear();
}

export async function transformLucide(code: MagicString, modules: ModuleDeclaration[]) {
  const lucideModules = modules
    .filter((decl) => decl.type === 'import' && targets.has(decl.module.path))
    .map((decl) => (decl as ImportDeclaration).module.path);

  const uniqueModules = [...new Set(lucideModules)];

  await Promise.all(
    uniqueModules.map(async (mod) => {
      if (iconsMap.has(mod)) {
        return;
      }

      const icons = await getIcons(mod);

      if (icons.length > 0) {
        iconsMap.set(mod, new Set(icons));
      }
    }),
  );

  for (const decl of modules) {
    if (decl.type === 'import' && targets.has(decl.module.path) && decl.namedImports) {
      let hasChanges = false;

      if (!iconsMap.has(decl.module.path)) {
        return;
      }

      const icons = iconsMap.get(decl.module.path)!;

      decl.namedImports.forEach((binding) => {
        if (!icons.has(binding.specifier)) {
          const nearestValidIcon = findNearestValidIcon(binding.specifier, icons);

          if (nearestValidIcon.length === 0) {
            return;
          }

          if (!binding.alias) {
            binding.alias = binding.specifier;
          }

          hasChanges = true;
          binding.specifier = nearestValidIcon;
        }
      });

      if (hasChanges) {
        code.replace(decl.startIndex, decl.endIndex, renderESM(decl));
      }
    }
  }
}

async function getIconsFromPath(library: string, extension: string, iconPath: string) {
  let path: string | undefined = undefined;

  const webcontainer = await webcontainerPromise;

  await getOutput(
    await webcontainer.spawn('node', [
      '-p',
      `require.resolve(${JSON.stringify(library)}).replace(${JSON.stringify(library + '.js')}, 'icons')`,
    ]),
    (output) => {
      if (output.startsWith('/home')) {
        path = removeWorkDir(output.trim());
      }
    },
  );

  const process = await webcontainer.spawn('ls', [path ?? iconPath]);

  let icons: string[] = [];
  await getOutput(process, (output) => {
    if (output.includes(`arrow-down.${extension}`)) {
      const list = output
        .trim()
        .split('\n')
        .map((icon) =>
          icon.replace(/\.([\s\S]*)$/, '').replace(/(-)?\w+/g, (str) => {
            const offset = +(str[0] === '-');
            return str[offset].toUpperCase() + str.slice(1 + offset);
          }),
        );

      icons = list.filter((element, index, array) => array.indexOf(element) === index);
    }
  });

  return icons;
}

async function getIcons(library: string) {
  if (library === 'lucide-svelte') {
    return getIconsFromPath(library, 'svelte', 'node_modules/lucide-svelte/dist/icons');
  }

  if (library === 'lucide-react-native') {
    return getIconsFromPath(library, 'js', 'node_modules/lucide-react-native/dist/cjs/icons');
  }

  const webcontainer = await webcontainerPromise;

  /**
   * Get the available icons from WebContainer.
   *
   * ESM is very slow so we have to force CJS.
   */
  const filter = library === 'lucide-solid' || library === '@lucide/lab' ? '' : ".filter(d => d[0] < 'a')";
  const cjsPathForce = library === '@lucide/lab' ? '' : '.replace(/.js$/, ".cjs")';

  const process = await webcontainer.spawn('node', [
    '--eval',
    stripIndents`
      const path = require.resolve(${JSON.stringify(library)});
      const cjsPath = path${cjsPathForce};

      fs.copyFileSync(path, cjsPath);

      const listIcons = (mod) => console.log(JSON.stringify(Object.keys(mod)${filter}));

      listIcons(require(cjsPath));`,
  ]);

  let icons: string[] = [];

  await getOutput(process, (output) => {
    icons = JSON.parse(output);
  });

  return icons!;
}

function findNearestValidIcon(name: string, icons: Set<string>): string {
  let maxValue = -1;
  let bestMatch = '';

  for (const iconName of icons) {
    const value = dice(iconName, name);

    if (value > maxValue) {
      maxValue = value;
      bestMatch = iconName;
    }
  }

  return bestMatch;
}
